import React, { useState } from 'react';
import api from '../../utils/api';
import { getCourseScheduleEntries } from '../../utils/courseSchedule';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

// Convert a course's schedule into a daySchedules array suitable for the form.
// Always uses the new format internally — falls back to legacy fields when loading existing courses.
const initDaySchedules = (course) => {
  const entries = getCourseScheduleEntries(course);
  return entries.map(e => ({
    day: e.day,
    startTime: e.startTime || '',
    endTime: e.endTime || ''
  }));
};
const FREQUENCIES = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly (3 months)' }
];

// One-time payment program types — Short Program (block) and Workshop. The
// student pays a single price upfront, no subscription, no recurring billing.
// Only Long Program (long_course) uses the Monthly + Quarterly subscription
// plans.
const isOneTimeType = (type) => type === 'workshop' || type === 'block';

const emptyBillingPlan = () => ({ name: '', frequency: 'monthly', amount: '', currency: 'USD', autoRenew: true, refundPolicy: 'none' });

const defaultBillingPlans = (existing, type) => {
  // Editing an existing course: keep what's there (pricing is locked once the
  // Stripe product is created anyway).
  if (existing?.length > 0) return existing.map(p => ({ ...p }));

  if (isOneTimeType(type)) {
    return [
      { name: 'Price', frequency: 'one_time', amount: '', currency: 'USD', autoRenew: false, refundPolicy: 'none' }
    ];
  }
  return [
    { name: 'Monthly', frequency: 'monthly', amount: '', currency: 'USD', autoRenew: true, refundPolicy: 'none' },
    { name: 'Quarterly', frequency: 'quarterly', amount: '', currency: 'USD', autoRenew: true, refundPolicy: 'none' }
  ];
};

const CourseForm = ({ studio, course, onSaved, onCancel, initialStep = 1 }) => {
  const isEdit = !!course;
  const [step, setStep] = useState(initialStep);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Pre-fill the inline discount controls from the (single) existing course code.
  const existingDiscount = course?.discountCodes?.[0] || null;

  const [form, setForm] = useState({
    name: course?.name || '',
    description: course?.description || '',
    danceStyle: course?.danceStyle || (studio?.danceStyles?.[0] || ''),
    levelName: course?.levelName || '',
    type: course?.type || 'long_course',
    schedule: {
      // New per-day format — source of truth in the form
      daySchedules: initDaySchedules(course),
      timezone: course?.schedule?.timezone || 'America/Chicago',
      sessions: course?.schedule?.sessions || [],
      // Block course fields
      blockStartDate: course?.schedule?.blockStartDate ? new Date(course.schedule.blockStartDate).toISOString().split('T')[0] : '',
      blockWeeks: course?.schedule?.blockWeeks || 8,
      // Workshop fields
      sessionDate: course?.schedule?.sessionDate ? new Date(course.schedule.sessionDate).toISOString().split('T')[0] : '',
      sessionStartTime: course?.schedule?.sessionStartTime || '',
      sessionEndTime: course?.schedule?.sessionEndTime || ''
    },
    location: {
      type: course?.location?.type || 'in_person',
      address: course?.location?.address || '',
      meetLink: course?.location?.meetLink || ''
    },
    capacity: course?.capacity || 20,
    billingPlans: defaultBillingPlans(course?.billingPlans, course?.type || 'long_course'),
    // One-time programs never have an advance deposit (no recurring "next month")
    requireAdvanceDeposit: isOneTimeType(course?.type || 'long_course')
      ? false
      : (course?.requireAdvanceDeposit !== false),
    materialReleaseDelay: course?.materialReleaseDelay || '2_days',
    status: course?.status || 'active',
    // Discount (inline, single code per course in v1)
    discountsEnabled: !!course?.discountsEnabled,
    discountCode: existingDiscount?.code || '',
    discountAmountOff: existingDiscount?.amountOff != null ? String(existingDiscount.amountOff) : '',
    discountMaxRedemptions: existingDiscount?.maxRedemptions != null ? String(existingDiscount.maxRedemptions) : '',
    discountExpiresAt: existingDiscount?.expiresAt ? new Date(existingDiscount.expiresAt).toISOString().split('T')[0] : ''
  });

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const updateNested = (parent, field, value) => {
    setForm(prev => ({ ...prev, [parent]: { ...prev[parent], [field]: value } }));
  };

  // When the user switches Program Type, reset billing plans to the right
  // shape: one-time = single Price field; long_course = Monthly + Quarterly.
  // Carry over the first plan's amount where possible so they don't lose what
  // they just typed if they realised the type is wrong.
  const updateType = (newType) => {
    setForm(prev => {
      const currentlyOneTime = isOneTimeType(prev.type);
      const nextOneTime = isOneTimeType(newType);
      if (currentlyOneTime === nextOneTime) {
        return { ...prev, type: newType };
      }
      const carry = prev.billingPlans?.[0]?.amount || '';
      const nextPlans = nextOneTime
        ? [{ name: 'Price', frequency: 'one_time', amount: carry, currency: 'USD', autoRenew: false, refundPolicy: 'none' }]
        : [
            { name: 'Monthly', frequency: 'monthly', amount: carry, currency: 'USD', autoRenew: true, refundPolicy: 'none' },
            { name: 'Quarterly', frequency: 'quarterly', amount: '', currency: 'USD', autoRenew: true, refundPolicy: 'none' }
          ];
      return {
        ...prev,
        type: newType,
        billingPlans: nextPlans,
        // No advance deposit on one-time programs.
        requireAdvanceDeposit: nextOneTime ? false : prev.requireAdvanceDeposit
      };
    });
  };

  // Toggle a day in daySchedules. When adding, copy time from the first existing entry
  // (if any) so multi-day courses default to the same time but can be edited per-day.
  const toggleDay = (day) => {
    setForm(prev => {
      const existing = prev.schedule.daySchedules || [];
      const isSelected = existing.some(d => d.day === day);
      let next;
      if (isSelected) {
        next = existing.filter(d => d.day !== day);
      } else {
        const seed = existing[0] || { startTime: '', endTime: '' };
        next = [...existing, { day, startTime: seed.startTime || '', endTime: seed.endTime || '' }];
      }
      return { ...prev, schedule: { ...prev.schedule, daySchedules: next } };
    });
  };

  const updateDayTime = (day, field, value) => {
    setForm(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        daySchedules: (prev.schedule.daySchedules || []).map(d =>
          d.day === day ? { ...d, [field]: value } : d
        )
      }
    }));
  };

  const updateBillingPlan = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      billingPlans: prev.billingPlans.map((p, i) => i === index ? { ...p, [field]: value } : p)
    }));
  };

  const addBillingPlan = () => {
    setForm(prev => ({ ...prev, billingPlans: [...prev.billingPlans, emptyBillingPlan()] }));
  };

  const removeBillingPlan = (index) => {
    setForm(prev => ({ ...prev, billingPlans: prev.billingPlans.filter((_, i) => i !== index) }));
  };

  // Block course sessions
  const addSession = () => {
    setForm(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        sessions: [...prev.schedule.sessions, { date: '', startTime: '', endTime: '' }]
      }
    }));
  };

  const updateSession = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        sessions: prev.schedule.sessions.map((s, i) => i === index ? { ...s, [field]: value } : s)
      }
    }));
  };

  const removeSession = (index) => {
    setForm(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        sessions: prev.schedule.sessions.filter((_, i) => i !== index)
      }
    }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        schedule: { ...form.schedule },
        billingPlans: form.billingPlans
          .filter(p => p.amount)
          .map(p => {
            const fallbackName = p.frequency === 'one_time' ? 'Price'
              : p.frequency === 'monthly' ? 'Monthly'
              : p.frequency === 'quarterly' ? 'Quarterly'
              : p.frequency;
            return {
              ...p,
              name: p.name || fallbackName,
              amount: parseFloat(p.amount)
            };
          }),
        requireAdvanceDeposit: form.requireAdvanceDeposit,
        capacity: parseInt(form.capacity) || 20,
        discountsEnabled: !!form.discountsEnabled
      };

      // Build the (single) course discount code from inline form fields.
      // If discounts are enabled and we have both a code + amount, persist as a one-element array.
      // Preserve existing redemptions when the code string is unchanged.
      if (form.discountsEnabled && form.discountCode && form.discountAmountOff) {
        const newCode = String(form.discountCode).trim().toUpperCase();
        const sameCode = existingDiscount && existingDiscount.code === newCode;
        const entry = {
          code: newCode,
          amountOff: parseFloat(form.discountAmountOff),
          currency: 'USD',
          expiresAt: form.discountExpiresAt ? new Date(form.discountExpiresAt) : null,
          maxRedemptions: form.discountMaxRedemptions ? parseInt(form.discountMaxRedemptions, 10) : null,
          notes: '',
          active: true,
          redemptions: sameCode ? (existingDiscount.redemptions || 0) : 0
        };
        if (sameCode) entry._id = existingDiscount._id;
        payload.discountCodes = [entry];
      } else {
        payload.discountCodes = [];
      }

      // Strip the inline discount fields — they're not part of the Course schema
      delete payload.discountCode;
      delete payload.discountAmountOff;
      delete payload.discountMaxRedemptions;
      delete payload.discountExpiresAt;

      // For long_course and block, mirror daySchedules → legacy fields for back-compat.
      // Require day + startTime + endTime — the API validator rejects entries with
      // any missing time field, so drop incomplete rows before sending.
      if (form.type === 'long_course' || form.type === 'block') {
        const valid = (form.schedule.daySchedules || []).filter(d => d.day && d.startTime && d.endTime);
        payload.schedule.daySchedules = valid;
        payload.schedule.days = valid.map(d => d.day);
        // If all entries share the same time, also write legacy startTime/endTime so older
        // read paths see the right value. Otherwise leave them as the first entry's time.
        const first = valid[0];
        if (first) {
          payload.schedule.startTime = first.startTime || '';
          payload.schedule.endTime = first.endTime || '';
        } else {
          payload.schedule.startTime = '';
          payload.schedule.endTime = '';
        }
      }

      // Clean schedule based on type
      if (form.type === 'long_course') {
        delete payload.schedule.sessions;
        delete payload.schedule.sessionDate;
        delete payload.schedule.sessionStartTime;
        delete payload.schedule.sessionEndTime;
        delete payload.schedule.blockStartDate;
        delete payload.schedule.blockWeeks;
      } else if (form.type === 'block') {
        // Block uses: blockStartDate, blockWeeks, daySchedules (+ mirrored legacy fields), timezone
        delete payload.schedule.sessions;
        delete payload.schedule.sessionDate;
        delete payload.schedule.sessionStartTime;
        delete payload.schedule.sessionEndTime;
      } else if (form.type === 'workshop') {
        delete payload.schedule.days;
        delete payload.schedule.startTime;
        delete payload.schedule.endTime;
        delete payload.schedule.daySchedules;
        delete payload.schedule.sessions;
        delete payload.schedule.blockStartDate;
        delete payload.schedule.blockWeeks;
      }

      let result;
      if (isEdit) {
        result = await api.put(`/courses/${course._id}`, payload);
      } else {
        result = await api.post('/courses', payload);
      }
      onSaved(result.data.course);
    } catch (err) {
      setError(err.response?.data?.details?.join(', ') || err.response?.data?.error || 'Failed to save course');
    } finally {
      setSaving(false);
    }
  };

  const canProceedStep1 = form.name && form.type;
  // Block "Next: Pricing" until each selected day has both start and end times.
  // The API validator rejects entries with empty endTime, so gate at the form level.
  const validDaySchedules = (form.schedule.daySchedules || []).filter(d => d.day && d.startTime && d.endTime);
  const canProceedStep2 = () => {
    if (form.type === 'long_course') return validDaySchedules.length > 0;
    if (form.type === 'block') return form.schedule.blockStartDate && validDaySchedules.length > 0 && form.schedule.blockWeeks > 0;
    if (form.type === 'workshop') return form.schedule.sessionDate;
    return true;
  };

  // Once Stripe products/prices are created, pricing must be locked.
  // Stripe prices are immutable — editing them would create a DB/Stripe mismatch
  // and existing subscriptions would still bill the old amount. Teachers should
  // create a new course if they need different pricing.
  const pricingLocked = isEdit && !!course?.stripeProductId;
  const canSubmit = form.billingPlans.some(p => p.amount);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {isEdit ? 'Edit Program' : 'Create Program'}
        </h1>
        <button onClick={onCancel} className="text-gray-500 hover:text-gray-700 text-sm font-medium">
          Cancel
        </button>
      </div>

      {/* Step indicator */}
      <div className="flex items-center justify-center sm:justify-start gap-1 sm:gap-2 mb-8">
        {[1, 2, 3].map(s => (
          <div key={s} className="flex items-center gap-1 sm:gap-2">
            <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-medium ${
              s <= step ? 'bg-brand-500 text-white' : 'bg-gray-200 text-gray-500'
            }`}>
              {s}
            </div>
            <span className={`text-xs sm:text-sm ${s <= step ? 'text-gray-900' : 'text-gray-400'}`}>
              {s === 1 ? 'Basics' : s === 2 ? 'Schedule' : 'Pricing'}
            </span>
            {s < 3 && <div className="w-6 sm:w-12 h-px bg-gray-300 mx-0.5 sm:mx-1" />}
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">

        {/* Step 1: Basics */}
        {step === 1 && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Program Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={e => update('name', e.target.value)}
                placeholder="e.g., Bharatanatyam Level 2 - Fall 2026"
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={form.description}
                onChange={e => update('description', e.target.value)}
                placeholder="What will students learn in this course?"
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Program Type *</label>
                <select
                  value={form.type}
                  onChange={e => updateType(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                >
                  <option value="long_course">Long Program (ongoing)</option>
                  <option value="block">Short Program (fixed weeks)</option>
                  <option value="workshop">Workshop (one-time)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <select
                  value={form.location.type}
                  onChange={e => updateNested('location', 'type', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                >
                  <option value="in_person">In-Person</option>
                  <option value="online">Online</option>
                  <option value="hybrid">Hybrid</option>
                </select>
              </div>
            </div>

            {(form.location.type === 'in_person' || form.location.type === 'hybrid') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <input
                  type="text"
                  value={form.location.address}
                  onChange={e => updateNested('location', 'address', e.target.value)}
                  placeholder="Studio address"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                />
              </div>
            )}

            {(form.location.type === 'online' || form.location.type === 'hybrid') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Meeting Link <span className="font-normal text-gray-400">(Zoom, Google Meet, Teams, etc.)</span>
                </label>
                <input
                  type="url"
                  value={form.location.meetLink}
                  onChange={e => updateNested('location', 'meetLink', e.target.value)}
                  placeholder="https://..."
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Paste a permanent meeting link here — students see it on their dashboard and in class-reminder emails. If your studio has Zoom connected via Integrations, the platform auto-creates meetings instead; leave this blank in that case.
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Dance Style</label>
                {studio?.danceStyles?.length > 0 ? (
                  <select
                    value={form.danceStyle}
                    onChange={e => update('danceStyle', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                  >
                    <option value="">Select...</option>
                    {studio.danceStyles.map(s => (
                      <option key={s} value={s} className="capitalize">{s}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={form.danceStyle}
                    onChange={e => update('danceStyle', e.target.value)}
                    placeholder="e.g., Bharatanatyam"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                  />
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Level</label>
                {studio?.levels?.length > 0 ? (
                  <select
                    value={form.levelName}
                    onChange={e => update('levelName', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                  >
                    <option value="">Select...</option>
                    {studio.levels
                      .sort((a, b) => a.order - b.order)
                      .map(l => (
                        <option key={l.name} value={l.name}>{l.name}</option>
                      ))
                    }
                  </select>
                ) : (
                  <input
                    type="text"
                    value={form.levelName}
                    onChange={e => update('levelName', e.target.value)}
                    placeholder="e.g., Stage 2"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                  />
                )}
              </div>
            </div>

            <div className="pt-4 flex justify-end">
              <button
                onClick={() => setStep(2)}
                disabled={!canProceedStep1}
                className="bg-brand-500 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-brand-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                Next: Schedule
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Schedule */}
        {step === 2 && (
          <div className="space-y-4">
            {form.type === 'long_course' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Class Days & Times *</label>
                  <p className="text-xs text-gray-500 mb-3">Click a day to add it. Each day can have a different start and end time.</p>
                  <div className="space-y-2">
                    {DAYS.map(day => {
                      const entry = (form.schedule.daySchedules || []).find(d => d.day === day);
                      const isSelected = !!entry;
                      return (
                        <div key={day} className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            onClick={() => toggleDay(day)}
                            className={`px-3 py-1.5 rounded-lg text-sm capitalize transition-colors w-24 ${
                              isSelected
                                ? 'bg-brand-500 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {day}
                          </button>
                          {isSelected && (
                            <>
                              <input
                                type="time"
                                value={entry.startTime || ''}
                                onChange={e => updateDayTime(day, 'startTime', e.target.value)}
                                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                              />
                              <span className="text-gray-400 text-sm">–</span>
                              <input
                                type="time"
                                value={entry.endTime || ''}
                                onChange={e => updateDayTime(day, 'endTime', e.target.value)}
                                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                              />
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
                  <select
                    value={form.schedule.timezone}
                    onChange={e => updateNested('schedule', 'timezone', e.target.value)}
                    className="w-full sm:w-1/3 border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                  >
                    <option value="America/New_York">Eastern (ET)</option>
                    <option value="America/Chicago">Central (CT)</option>
                    <option value="America/Denver">Mountain (MT)</option>
                    <option value="America/Los_Angeles">Pacific (PT)</option>
                    <option value="Asia/Kolkata">India (IST)</option>
                  </select>
                </div>
              </>
            )}

            {form.type === 'block' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
                    <input
                      type="date"
                      value={form.schedule.blockStartDate}
                      onChange={e => updateNested('schedule', 'blockStartDate', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Duration (weeks) *</label>
                    <input
                      type="number"
                      value={form.schedule.blockWeeks}
                      onChange={e => updateNested('schedule', 'blockWeeks', parseInt(e.target.value) || '')}
                      min="1"
                      max="52"
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Class Days & Times *</label>
                  <p className="text-xs text-gray-500 mb-3">Click a day to add it. Each day can have a different start and end time.</p>
                  <div className="space-y-2">
                    {DAYS.map(day => {
                      const entry = (form.schedule.daySchedules || []).find(d => d.day === day);
                      const isSelected = !!entry;
                      return (
                        <div key={day} className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            onClick={() => toggleDay(day)}
                            className={`px-3 py-1.5 rounded-lg text-sm capitalize transition-colors w-24 ${
                              isSelected
                                ? 'bg-brand-500 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {day}
                          </button>
                          {isSelected && (
                            <>
                              <input
                                type="time"
                                value={entry.startTime || ''}
                                onChange={e => updateDayTime(day, 'startTime', e.target.value)}
                                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                              />
                              <span className="text-gray-400 text-sm">–</span>
                              <input
                                type="time"
                                value={entry.endTime || ''}
                                onChange={e => updateDayTime(day, 'endTime', e.target.value)}
                                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                              />
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
                  <select
                    value={form.schedule.timezone}
                    onChange={e => updateNested('schedule', 'timezone', e.target.value)}
                    className="w-full sm:w-1/3 border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                  >
                    <option value="America/New_York">Eastern (ET)</option>
                    <option value="America/Chicago">Central (CT)</option>
                    <option value="America/Denver">Mountain (MT)</option>
                    <option value="America/Los_Angeles">Pacific (PT)</option>
                    <option value="Asia/Kolkata">India (IST)</option>
                  </select>
                </div>
                {form.schedule.blockStartDate && validDaySchedules.length > 0 && form.schedule.blockWeeks > 0 && (
                  <p className="text-xs text-gray-500">
                    {form.schedule.blockWeeks} weeks &middot; {validDaySchedules.length} day{validDaySchedules.length > 1 ? 's' : ''}/week &middot; {form.schedule.blockWeeks * validDaySchedules.length} total sessions
                  </p>
                )}
              </>
            )}

            {form.type === 'workshop' && (
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                  <input
                    type="date"
                    value={form.schedule.sessionDate}
                    onChange={e => updateNested('schedule', 'sessionDate', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                  <input
                    type="time"
                    value={form.schedule.sessionStartTime}
                    onChange={e => updateNested('schedule', 'sessionStartTime', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                  <input
                    type="time"
                    value={form.schedule.sessionEndTime}
                    onChange={e => updateNested('schedule', 'sessionEndTime', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Material Release Delay</label>
              <select
                value={form.materialReleaseDelay}
                onChange={e => update('materialReleaseDelay', e.target.value)}
                className="w-48 border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
              >
                <option value="immediately">Immediately after class</option>
                <option value="1_day">1 day after class</option>
                <option value="2_days">2 days after class</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">When students unlock tutorial videos after attending a class.</p>
            </div>

            <div className="pt-4 flex justify-between">
              <button
                onClick={() => setStep(1)}
                className="text-gray-600 px-6 py-2.5 rounded-lg font-medium hover:bg-gray-100 transition-colors"
              >
                Back
              </button>
              <button
                onClick={() => setStep(3)}
                disabled={!canProceedStep2()}
                className="bg-brand-500 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-brand-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                Next: Pricing
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Pricing */}
        {step === 3 && (
          <div className="space-y-4">
            {pricingLocked && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm font-medium text-amber-800">Pricing is locked</p>
                <p className="text-xs text-amber-700 mt-1">
                  Stripe prices are immutable once created. To change pricing, archive this course and create a new one — existing subscribers will keep their current rate.
                </p>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-3 block">Pricing *</label>
              <p className="text-xs text-gray-500 mb-4">
                {isOneTimeType(form.type)
                  ? 'Set the one-time price for this program. Students pay once at enrollment. You keep 100% of the price you set.'
                  : 'Set your price for each billing cycle. Students choose their preferred plan at checkout. You keep 100% of the price you set.'}
              </p>

              {isOneTimeType(form.type) ? (
                /* One-time payment — single Price field for Workshop / Short Program */
                (() => {
                  const plan = form.billingPlans[0] || { amount: '' };
                  const amt = parseFloat(plan.amount) || 0;
                  const studentPrice = amt > 0 ? Math.ceil(amt * 1.06) : 0;
                  return (
                    <div className={`p-4 rounded-lg max-w-md ${pricingLocked ? 'bg-gray-100' : 'bg-gray-50'}`}>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Price (one-time)</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                        <input
                          type="number"
                          value={plan.amount || ''}
                          onChange={e => updateBillingPlan(0, 'amount', e.target.value)}
                          placeholder={form.type === 'workshop' ? '50' : '300'}
                          min="0"
                          step="1"
                          disabled={pricingLocked}
                          className={`w-full border border-gray-300 rounded-lg pl-7 pr-3 py-2.5 text-sm focus:ring-2 focus:ring-brand-400 focus:border-transparent ${pricingLocked ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : ''}`}
                        />
                      </div>
                      {studentPrice > 0 && (
                        <p className="text-xs text-gray-500 mt-1.5">
                          Student pays <span className="font-semibold text-gray-700">${studentPrice}</span>
                          {' '}(one-time)
                        </p>
                      )}
                    </div>
                  );
                })()
              ) : (
                /* Long Program — Monthly + Quarterly subscription plans */
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  {form.billingPlans.map((plan, i) => {
                    const amt = parseFloat(plan.amount) || 0;
                    const studentPrice = amt > 0 ? Math.ceil(amt * 1.06) : 0;
                    return (
                      <div key={plan.frequency} className={`p-4 rounded-lg ${pricingLocked ? 'bg-gray-100' : 'bg-gray-50'}`}>
                        <label className="block text-sm font-semibold text-gray-800 mb-2">
                          {plan.frequency === 'monthly' ? 'Monthly' : 'Quarterly (3 months)'}
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                          <input
                            type="number"
                            value={plan.amount}
                            onChange={e => updateBillingPlan(i, 'amount', e.target.value)}
                            placeholder={plan.frequency === 'monthly' ? '100' : '270'}
                            min="0"
                            step="1"
                            disabled={pricingLocked}
                            className={`w-full border border-gray-300 rounded-lg pl-7 pr-3 py-2.5 text-sm focus:ring-2 focus:ring-brand-400 focus:border-transparent ${pricingLocked ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : ''}`}
                          />
                        </div>
                        {studentPrice > 0 && (
                          <p className="text-xs text-gray-500 mt-1.5">
                            Student pays <span className="font-semibold text-gray-700">${studentPrice}</span>
                            {plan.frequency === 'monthly' ? '/mo' : '/quarter'}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Advance Deposit Checkbox — subscriptions only */}
            {!isOneTimeType(form.type) && (
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="advanceDeposit"
                  checked={form.requireAdvanceDeposit}
                  onChange={e => update('requireAdvanceDeposit', e.target.checked)}
                  disabled={pricingLocked}
                  className={`mt-1 accent-brand-500 w-4 h-4 ${pricingLocked ? 'cursor-not-allowed opacity-50' : ''}`}
                />
                <label htmlFor="advanceDeposit" className={`text-sm ${pricingLocked ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                  <span className={`font-medium ${pricingLocked ? 'text-gray-500' : 'text-gray-700'}`}>Require advance deposit (1 month)</span>
                  <span className="block text-xs text-gray-500">Collected at enrollment, applied to student's last month when they give notice</span>
                </label>
              </div>
            )}

            {/* Pricing Preview */}
            {form.billingPlans.some(p => p.amount) && (
              <div className="bg-brand-50 border border-brand-100 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Student pricing preview</p>
                <div className="space-y-1.5">
                  {form.billingPlans.filter(p => p.amount).map((plan, i) => {
                    const amt = parseFloat(plan.amount) || 0;
                    const studentPrice = Math.ceil(amt * 1.06);

                    if (plan.frequency === 'one_time') {
                      return (
                        <div key={i} className="text-sm">
                          <span className="font-medium text-gray-800">One-time</span>
                          <span className="text-gray-600">
                            {' '}&mdash; <span className="font-semibold text-gray-900">${studentPrice}</span>
                            <span className="text-xs text-gray-500"> at enrollment</span>
                          </span>
                        </div>
                      );
                    }

                    const monthlyPlan = form.billingPlans.find(p => p.frequency === 'monthly' && parseFloat(p.amount) > 0);
                    const monthlyAmt = parseFloat(monthlyPlan?.amount) || amt;
                    const hasAdvance = form.requireAdvanceDeposit;
                    const enrollmentTotal = hasAdvance ? Math.ceil((amt + monthlyAmt) * 1.06) : studentPrice;
                    const advanceStudentPrice = hasAdvance ? Math.ceil(monthlyAmt * 1.06) : 0;
                    const freq = plan.frequency === 'monthly' ? '/mo' : '/quarter';

                    return (
                      <div key={i} className="text-sm">
                        <span className="font-medium text-gray-800">{plan.frequency === 'monthly' ? 'Monthly' : 'Quarterly'}</span>
                        {hasAdvance ? (
                          <span className="text-gray-600">
                            {' '}&mdash; Enrollment: <span className="font-semibold text-gray-900">${enrollmentTotal}</span>
                            <span className="text-xs text-gray-500"> (incl. ${advanceStudentPrice} advance deposit)</span>
                            {' '}&middot; Then <span className="font-semibold text-gray-900">${studentPrice}{freq}</span>
                          </span>
                        ) : (
                          <span className="text-gray-600">
                            {' '}&mdash; <span className="font-semibold text-gray-900">${studentPrice}{freq}</span>
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}


            {/* Discounts (inline) */}
            <div className="border-t border-gray-100 pt-4">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="discountsEnabled"
                  checked={!!form.discountsEnabled}
                  onChange={e => update('discountsEnabled', e.target.checked)}
                  className="mt-1 accent-brand-500 w-4 h-4 cursor-pointer"
                />
                <label htmlFor="discountsEnabled" className="text-sm cursor-pointer">
                  <span className="font-medium text-gray-700">Allow a discount code for this course</span>
                  <span className="block text-xs text-gray-500">By checking this, both course-specific and studio family discount codes will be accepted at this course's checkout. Parents and students will see an "Enter discount code" option at checkout.</span>
                </label>
              </div>

              {!studio?.discountsEnabled && form.discountsEnabled && (
                <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                  Discount codes are turned off studio-wide. Enable them in Settings &rarr; Discounts before students will see this code.
                </div>
              )}

              {form.discountsEnabled && (
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount off ($)</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                      <input
                        type="number"
                        value={form.discountAmountOff}
                        onChange={e => update('discountAmountOff', e.target.value)}
                        placeholder="Enter amount"
                        min="0.01"
                        step="1"
                        className="w-full border border-gray-300 rounded-lg pl-7 pr-3 py-2 text-sm focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                    <input
                      type="text"
                      value={form.discountCode}
                      onChange={e => update('discountCode', e.target.value.toUpperCase())}
                      placeholder="Enter code (e.g. SUMMER20)"
                      maxLength={20}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono uppercase focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-400 mt-1">3–20 alphanumeric characters.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Expires (optional)</label>
                    <input
                      type="date"
                      value={form.discountExpiresAt}
                      onChange={e => update('discountExpiresAt', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max redemptions (optional)</label>
                    <input
                      type="number"
                      value={form.discountMaxRedemptions}
                      onChange={e => update('discountMaxRedemptions', e.target.value)}
                      placeholder="Unlimited"
                      min="1"
                      step="1"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                    />
                  </div>
                </div>
              )}

              {form.discountsEnabled && existingDiscount && (existingDiscount.redemptions > 0) && (
                <p className="mt-3 text-xs text-gray-500">
                  Used {existingDiscount.redemptions}{existingDiscount.maxRedemptions != null ? ` / ${existingDiscount.maxRedemptions}` : ''} times.
                  Changing the code resets the count.
                </p>
              )}
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            <div className="pt-4 flex justify-between">
              <button
                onClick={() => setStep(2)}
                className="text-gray-600 px-6 py-2.5 rounded-lg font-medium hover:bg-gray-100 transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving || !canSubmit}
                className="bg-brand-500 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-brand-600 disabled:bg-gray-300 transition-colors"
              >
                {saving ? 'Saving...' : isEdit ? 'Update Program' : 'Create Program'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseForm;
